import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../dataManagement/uiDb/UiDb";
import { setNativePermission } from "../dataManagement/uiDb/NotificationsDb";
import { pullAndRegisterNativeToken } from "../services/native/nativeBridge";
import {
  callOnWebAppReady,
  getNotificationPermissionState,
} from "../services/native/androidBridge";
import {
  callOnWebAppReadyIos,
  iosBridgeAvailable,
} from "../services/native/iosBridge";
import { isAndroidWebView, isIosWebView } from "../services/helpers/platform";
import { bridgeLog } from "../services/native/bridgeLog";

/**
 * Web side of the native bridge handshake (Android + iOS).
 *
 * window.NineFindsNative is installed at startup in main.tsx. Once the user is
 * logged in (and on every login), this hook signals native readiness so native
 * can push the FCM token + permission state into the web layer.
 *
 * The two platforms differ because their web -> native channels differ:
 *   - Android's @JavascriptInterface returns values synchronously, so we PULL:
 *     signal onWebAppReady(), then read getFcmToken() / getNotificationState()
 *     and register / seed them here.
 *   - iOS's WKWebView postMessage is fire-and-forget (no return value), so we
 *     PUSH: signal onWebAppReady() and native responds by calling
 *     window.NineFindsNative.registerFcmToken() / onPermissionChanged().
 *     Nothing is pulled here.
 *
 * Neither platform auto-requests permission — that's button-driven (native
 * shows the initial OS prompt on first login; re-asks come from the banner).
 * Detects the app via the injected bridge or the UA marker, polling briefly for
 * the interface. Inert on a normal browser.
 */
export function useNativeBridge() {
  const dispatch = useDispatch<AppDispatch>();
  const profile = useSelector((s: RootState) => s.profile.data);
  const doneRef = useRef(false);

  useEffect(() => {

    const android = isAndroidWebView() || typeof window.AndroidBridge !== "undefined";
    const ios = !android && (isIosWebView() || iosBridgeAvailable());
    if (!android && !ios) return;

    // Re-arm on logout so the next login runs again.
    if (!profile) {
      doneRef.current = false;
      return;
    }
    if (doneRef.current) return;

    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    // Has the native handshake interface actually been injected yet?
    const bridgeReady = () =>
      android
        ? typeof window.AndroidBridge?.onWebAppReady === "function"
        : iosBridgeAvailable();

    const run = () => {
      if (doneRef.current) return;

      if (bridgeReady()) {
        doneRef.current = true;

        if (android) {
          callOnWebAppReady();
          bridgeLog("onWebAppReady() called (android)");

          // Android returns values synchronously — pull the token now and seed
          // the permission state so the banner reflects reality immediately.
          pullAndRegisterNativeToken();
          dispatch(setNativePermission(getNotificationPermissionState()));
        } else {
          callOnWebAppReadyIos();
          bridgeLog("onWebAppReady() called (ios)");

          // iOS can't return values synchronously: native responds to the ready
          // signal by pushing the FCM token (NineFindsNative.registerFcmToken)
          // and the permission state (onPermissionChanged). Nothing to pull.
        }
        return;
      }

      // Bridge not injected yet — retry for ~6s.
      if (attempts++ < 20) {
        timer = setTimeout(run, 300);
      } else {
        console.warn("[bridge] native onWebAppReady never appeared");
      }
    };

    run();
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [dispatch, profile]);
}
